import React from "react";
import { toast } from "react-toastify";
import { Copy } from "lucide-react";
import { shareProfileOnWhatsApp } from "../../helpers/profileExport.helper";
import ShareModal from "../../components/ShareModal";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { RootState } from "../../store";
import { appendWebsiteLinks } from "../../helpers/loginCredentials";

type UserData = {
  martialId?: string;
  password?: string;
  fullName?: string;
  mobileNumber?: string;
  [key: string]: any;
} | null;

type ProfileCompletionSuccessProps = {
  userData: UserData;
  onGoToDashboard: () => void;
  showGoToDashboard?: boolean;
  showShareActions?: boolean;
  onClose?: () => void;
  whatsAppShareMode?: "direct" | "options";
};

const ProfileCompletionSuccess: React.FC<ProfileCompletionSuccessProps> = ({
  userData,
  onGoToDashboard,
  showGoToDashboard = true,
  showShareActions = true,
  onClose,
  whatsAppShareMode = "direct",
}) => {
  const [isShareModalOpen, setIsShareModalOpen] = React.useState(false);
  const bureauInfo = useSelector((state: RootState) => state.bureau.data);
  const isBureauDashboard = useLocation().pathname.startsWith("/admin");

  React.useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const handleCopyValue = async (label: string, value?: string) => {
    if (!value) {
      toast.error(`${label} not available.`);
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied.`);
    } catch (error) {
      console.error(`Failed to copy ${label}`, error);
      toast.error(`Failed to copy ${label}.`);
    }
  };

  const handleShareLoginDetails = async () => {
    if (!userData) return;

    // Only the logged-in bureau's links, and only inside the bureau dashboard
    // (this popup is also used by public self-registration).
    const loginDetails = appendWebsiteLinks(
      `Martial ID: ${userData.martialId ?? "N/A"}\nPassword: ${
        userData.password ?? "N/A"
      }${
        userData.mobileNumber ? `\nMobile Number: ${userData.mobileNumber}` : ""
      }`,
      isBureauDashboard ? bureauInfo?.websiteLinks : undefined,
    );

    if (navigator.share) {
      try {
        await navigator.share({
          title: "Login Details",
          text: loginDetails,
        });
      } catch (error) {
        console.error("Login details share cancelled or failed:", error);
      }

      return;
    }

    try {
      await navigator.clipboard.writeText(loginDetails);
      toast.success("Login details copied.");
    } catch (error) {
      console.error("Failed to copy login details:", error);
      toast.error("Unable to share login details.");
    }
  };

  const handleShareViaWhatsApp = async () => {
    if (!userData) return;

    if (whatsAppShareMode === "options") {
      setIsShareModalOpen(true);
      return;
    }

    try {
      await shareProfileOnWhatsApp(userData);
      toast.success("Opening WhatsApp share...");
    } catch (error) {
      console.error("WhatsApp share failed:", error);
      toast.error("Unable to open WhatsApp share.");
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
      return;
    }

    onGoToDashboard();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/45 p-3 sm:p-4">
      <div className="flex min-h-full items-start justify-center sm:items-center">
        <div className="my-3 flex w-full max-w-xl flex-col overflow-hidden rounded-[28px] bg-white shadow-2xl ring-1 ring-black/5 sm:my-0">
          {/* Header */}
          <div className="shrink-0 bg-gradient-to-r from-emerald-50 via-white to-blue-50 px-5 pb-5 pt-4 sm:px-7 sm:pb-6 sm:pt-6">
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleClose}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-white/80 hover:text-slate-700"
                aria-label="Close success popup"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-500 shadow-lg shadow-green-200">
                <svg
                  className="h-8 w-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>

              <h1 className="text-2xl font-bold leading-tight text-green-600 sm:text-4xl">
                Profile Successfully Completed!
              </h1>

              <p className="mx-auto mt-3 max-w-md text-sm text-gray-600 sm:text-base">
                Your customer profile has been successfully created.
              </p>
            </div>
          </div>

          {/* Scrollable body */}
          <div className="max-h-[calc(100dvh-16rem)] overflow-y-auto overscroll-contain px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-4 sm:max-h-[calc(100dvh-14rem)] sm:px-7 sm:pb-6">
            {userData &&
              (userData.martialId ||
                userData.password ||
                userData.mobileNumber) && (
                <div className="mb-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <h2 className="mb-3 text-center text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">
                    Your Login Details
                  </h2>

                  <div className="grid gap-3 sm:grid-cols-2">
                    {userData.martialId && (
                      <div className="rounded-xl bg-white px-4 py-3 text-center shadow-sm ring-1 ring-slate-100">
                        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                          Martial ID
                        </div>

                        <div className="mt-1 flex items-center justify-center gap-2">
                          <div className="break-all font-mono text-base font-semibold text-green-600">
                            {userData.martialId}
                          </div>

                          <button
                            type="button"
                            onClick={() =>
                              handleCopyValue("Martial ID", userData.martialId)
                            }
                            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-green-50 text-green-600 transition hover:bg-green-100"
                            aria-label="Copy martial ID"
                          >
                            <Copy size={15} />
                          </button>
                        </div>
                      </div>
                    )}

                    {userData.password && (
                      <div className="rounded-xl bg-white px-4 py-3 text-center shadow-sm ring-1 ring-slate-100">
                        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                          Password
                        </div>

                        <div className="mt-1 flex items-center justify-center gap-2">
                          <div className="break-all font-mono text-base font-semibold text-blue-600">
                            {userData.password}
                          </div>

                          <button
                            type="button"
                            onClick={() =>
                              handleCopyValue("Password", userData.password)
                            }
                            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600 transition hover:bg-blue-100"
                            aria-label="Copy password"
                          >
                            <Copy size={15} />
                          </button>
                        </div>
                      </div>
                    )}

                    {userData.mobileNumber && (
                      <div className="rounded-xl bg-white px-4 py-3 text-center shadow-sm ring-1 ring-slate-100 sm:col-span-2">
                        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                          Mobile Number
                        </div>

                        <div className="mt-1 flex items-center justify-center gap-2">
                          <div className="break-all font-mono text-base font-semibold text-violet-600">
                            {userData.mobileNumber}
                          </div>

                          <button
                            type="button"
                            onClick={() =>
                              handleCopyValue(
                                "Mobile number",
                                userData.mobileNumber,
                              )
                            }
                            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-violet-50 text-violet-600 transition hover:bg-violet-100"
                            aria-label="Copy mobile number"
                          >
                            <Copy size={15} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

            <div className="space-y-3">
              {showShareActions && (
                <>
                  <button
                    type="button"
                    onClick={handleShareViaWhatsApp}
                    className="w-full rounded-xl bg-green-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-green-700"
                  >
                    Share via WhatsApp
                  </button>

                  <button
                    type="button"
                    onClick={handleShareLoginDetails}
                    className="flex w-full items-center justify-center rounded-xl bg-blue-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-blue-700"
                  >
                    <svg
                      className="mr-2 h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"
                      />
                    </svg>
                    Share Login Details
                  </button>
                </>
              )}

              {showGoToDashboard && (
                <button
                  type="button"
                  onClick={onGoToDashboard}
                  className="w-full rounded-xl bg-slate-900 px-6 py-3 font-semibold text-white transition-colors hover:bg-slate-800"
                >
                  Go to Dashboard
                </button>
              )}
            </div>

            <div className="mt-5 text-center text-sm text-gray-500">
              <p>Please save your login details securely.</p>
              <p>
                You can use these credentials to access your profile anytime.
              </p>
            </div>
          </div>
        </div>
      </div>

      {showShareActions && isShareModalOpen && userData && (
        <ShareModal
          profile={userData}
          setIsShareModalOpen={setIsShareModalOpen}
        />
      )}
    </div>
  );
};

export default ProfileCompletionSuccess;
